import { t } from "@lingui/macro";
import { NavigationProp, useNavigation } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { Suspense, useCallback, useEffect, useState } from "react";
import { Image } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { MenuIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Pressable,
  Text,
} from "@whoosh/design-system/src/components/primitive";
import { getThumbnail } from "@whoosh/utilities/src/Cloudinary";

import { AuthenticationModal } from "../../features/Authentication/components/AuthenticationModal";
import { LanguageSelectContainer } from "../../features/LanguagePreference";
import {
  sessionCurrentClubAtom,
  sessionCurrentClubSlugAtom,
} from "../../features/Session";
import { BaseNavigatorParamList } from "../../navigators/BaseNavigator/types";
import { TopbarQuery as TopbarQueryType } from "./__generated__/TopbarQuery.graphql";
import { NavigationMenu } from "./components/NavigationMenu/NavigationMenu";
import { TopbarUserContent } from "./components/TopbarUserContent/TopbarUserContent";

export const Topbar = () => {
  const [queryRef, loadQuery] =
    useQueryLoader<TopbarQueryType>(TopbarQueryString);

  useEffect(() => {
    loadQuery({});
  }, [loadQuery]);

  const handleRefetch = useCallback(() => {
    loadQuery({}, { fetchPolicy: "network-only" });
  }, [loadQuery]);

  if (!queryRef) {
    return <TopbarPlaceholder />;
  }

  return (
    <Suspense fallback={<TopbarPlaceholder />}>
      <TopbarContent queryRef={queryRef} onRefetch={handleRefetch} />
    </Suspense>
  );
};

const TopbarPlaceholder = () => (
  <HStack
    testID="topbar-placeholder"
    paddingHorizontal={6}
    paddingVertical={4}
    justifyContent="space-between"
    alignItems="center"
  >
    <Box width={40} height={40} />
  </HStack>
);

type TopbarContentProps = {
  queryRef: PreloadedQuery<TopbarQueryType>;
  onRefetch: () => void;
};

export const TopbarContent = ({ queryRef, onRefetch }: TopbarContentProps) => {
  const navigation = useNavigation<NavigationProp<BaseNavigatorParamList>>();

  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  const data = usePreloadedQuery(TopbarQueryString, queryRef);

  const club = useAtomValue(sessionCurrentClubAtom);
  const clubSlug = useAtomValue(sessionCurrentClubSlugAtom) ?? "";

  const clubName = club?.name;
  const clubLogoPublicId = club?.logoImage;

  const isSignedIn = !!data.session?.user?.id;
  const member = data.session?.currentClientProfile?.member;
  const isMember = !!member?.id;

  const user = data.session?.user;

  const firstName = member?.firstName ?? user?.firstName ?? null;
  const lastName = member?.lastName ?? user?.lastName ?? null;
  const profileImagePublicId = member?.profileImage?.publicId;

  const handleAuthComplete = useCallback(() => {
    setIsAuthModalOpen(false);
    onRefetch();
  }, [onRefetch]);

  const handleHomePress = () => {
    if (!clubSlug) return;

    navigation.navigate("Club", {
      clubSlug,
      screen: "Home",
      params: { screen: "MainHome" },
    });
  };

  const handleProfilePress = () => {
    if (!clubSlug) return;

    navigation.navigate("Club", {
      clubSlug,
      screen: "Home",
      params: {
        screen: "Profile",
        params: { screen: "ProfileHome" },
      },
    });
  };

  return (
    <>
      <HStack
        testID="topbar"
        paddingHorizontal={4}
        paddingVertical={4}
        justifyContent="space-between"
        alignItems="center"
        gap={3}
      >
        <HStack alignItems="center" gap={4} flex={1} minWidth={0}>
          <Button
            variant="ghost"
            size="small"
            onPress={() => setIsMenuOpen(true)}
            testID="topbar-menu"
            accessibilityLabel={t`Menu`}
            leftIcon={<MenuIcon color="text-primary" />}
          />
          <Pressable
            onPress={handleHomePress}
            testID="topbar-club-branding"
            accessibilityRole="button"
            accessibilityLabel={
              clubName ? t`${clubName}, go to home` : t`Go to home`
            }
            style={{ flexShrink: 1, minWidth: 0 }}
          >
            <HStack alignItems="center" gap={4} flexShrink={1} minWidth={0}>
              {clubLogoPublicId ? (
                <Image
                  source={{
                    uri: getThumbnail(clubLogoPublicId, 40, 40)?.toURL(),
                  }}
                  accessibilityLabel={
                    clubName ? t`${clubName} logo` : t`Club Logo`
                  }
                  testID="topbar-club-logo"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 8,
                    backgroundColor: "#FFFFFF",
                  }}
                />
              ) : (
                <Box
                  width={40}
                  height={40}
                  borderRadius="1"
                  backgroundColor="surface-secondary"
                  testID="topbar-club-logo-placeholder"
                  flexShrink={0}
                />
              )}
              {clubName ? (
                <Text
                  variant="mainBaseSemiBold"
                  color="text-primary"
                  numberOfLines={1}
                  testID="topbar-club-name"
                  flexShrink={1}
                >
                  {clubName}
                </Text>
              ) : null}
            </HStack>
          </Pressable>
        </HStack>

        <HStack alignItems="center" gap={2}>
          <LanguageSelectContainer
            syncToUserProfile={isSignedIn}
            testID="topbar-language-select"
          />
          <TopbarUserContent
            isSignedIn={isSignedIn}
            isMember={isMember}
            firstName={firstName}
            lastName={lastName}
            profileImagePublicId={profileImagePublicId}
            onSignIn={() => setIsAuthModalOpen(true)}
            onProfilePress={handleProfilePress}
          />
        </HStack>
      </HStack>
      <AuthenticationModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onComplete={handleAuthComplete}
        clubSlug={clubSlug}
      />
      {data.session ? (
        <NavigationMenu
          isOpen={isMenuOpen}
          onClose={() => setIsMenuOpen(false)}
          sessionRef={data.session}
          isMember={isMember}
          onSignIn={() => setIsAuthModalOpen(true)}
        />
      ) : null}
    </>
  );
};

export const TopbarQueryString = graphql`
  query TopbarQuery {
    session {
      ...NavigationMenu_session
      user {
        id
        ... on MemberUser {
          firstName
          lastName
        }
      }
      currentClientProfile {
        member {
          ... on Member {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          ... on Customer {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
        }
      }
    }
  }
`;
